<script setup lang="ts">
import { Icon } from '@iconify/vue';
/**
 * UiCheckbox - Design System Checkbox Component
 *
 * A checkbox input with label support and consistent styling.
 *
 * @example
 * <UiCheckbox v-model="agreed" label="I agree to the terms" />
 */
import { computed, useId } from 'vue';
import { IconLib } from '@/assets/icon-library';

export interface UiCheckboxProps {
   /** Visible label text */
   label?: string;
   /** Accessible label for checkboxes without a visible label (icon-only / inline contexts). Rendered as sr-only text inside the label so screen readers and a11y scanners see a non-empty form label. */
   ariaLabel?: string;
   /** Disabled state */
   disabled?: boolean;
   /** Indeterminate state */
   indeterminate?: boolean;
   /** Size variant */
   size?: 'sm' | 'md' | 'lg';
   /** Error message */
   errorMessage?: string;
}

const props = withDefaults(defineProps<UiCheckboxProps>(), {
   disabled: false,
   indeterminate: false,
   size: 'md',
});

const modelValue = defineModel<boolean>();
const id = useId();

const checkboxClasses = computed(() => {
   const sizeClasses = {
      sm: 'size-4',
      md: 'size-5',
      lg: 'size-6',
   };

   const baseClasses = [
      'relative flex items-center justify-center rounded-sm border-2 transition-colors cursor-pointer',
      'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary has-[:focus-visible]:ring-offset-2',
      sizeClasses[props.size],
   ];

   if (props.disabled) {
      baseClasses.push('opacity-50 cursor-not-allowed');
   }

   if (modelValue.value || props.indeterminate) {
      baseClasses.push('bg-primary border-primary');
   }
   else {
      baseClasses.push('border-neutral-dark bg-white hover:border-primary');
   }

   if (props.errorMessage) {
      baseClasses.push('border-accent');
   }

   return baseClasses.join(' ');
});

const iconSize = computed(() => {
   const sizes = { sm: 'size-3', md: 'size-4', lg: 'size-5' };
   return sizes[props.size];
});

const labelClasses = computed(() => {
   const sizeClasses = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
   };
   return [
      'select-none',
      sizeClasses[props.size],
      props.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
   ].join(' ');
});
</script>

<template>
   <div class="flex flex-col gap-1">
      <label :for="id" class="inline-flex items-center gap-3">
         <span :class="checkboxClasses">
            <input
               :id="id"
               v-model="modelValue"
               type="checkbox"
               :disabled="disabled"
               :indeterminate="indeterminate"
               class="sr-only peer"
            />
            <Icon
               v-if="modelValue && !indeterminate"
               :icon="IconLib.actions.check"
               :class="iconSize"
               class="text-white"
            />
            <Icon
               v-else-if="indeterminate"
               :icon="IconLib.actions.minus"
               :class="iconSize"
               class="text-white"
            />
         </span>
         <span v-if="label" :class="labelClasses">
            {{ label }}
         </span>
         <span v-else-if="ariaLabel" class="sr-only">
            {{ ariaLabel }}
         </span>
      </label>
      <span v-if="errorMessage" class="text-sm text-accent ml-8">
         {{ errorMessage }}
      </span>
   </div>
</template>
